import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Mail,
  MousePointerClick,
  Phone,
  ShieldCheck,
  Plug,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { EncrelioMark } from "@/components/Logo";
import { appConfig } from "@/config/app";
import { cn } from "@/lib/utils";
import { BRAND } from "../brand";
import { SectionHeading } from "../SectionHeading";
import { Reveal } from "../motion/Reveal";
import { useInViewOnce } from "../motion/useInViewOnce";

type Lane = {
  label: string;
  /** Vers la droite (ou le bas) : de la source vers la cible. */
  forward: boolean;
  /** Automatique : pointillés. Action manuelle : trait plein. */
  auto: boolean;
};

const PENNYLANE_LANES: Lane[] = [
  { label: "Lecture seule, chaque heure", forward: true, auto: true },
];
const GMAIL_LANES: Lane[] = [
  { label: "Quand vous cliquez sur Envoyer", forward: true, auto: false },
  { label: "Réponses rangées, toutes les 5 min", forward: false, auto: true },
];

const GEOMETRY = {
  horizontal: {
    viewBox: "0 0 150 14",
    forward: { line: "M3 7 H139", head: "M133 2 L141 7 L133 12" },
    backward: { line: "M147 7 H11", head: "M17 2 L9 7 L17 12" },
  },
  vertical: {
    viewBox: "0 0 14 64",
    forward: { line: "M7 3 V53", head: "M2 47 L7 55 L12 47" },
    backward: { line: "M7 61 V11", head: "M2 17 L7 9 L12 17" },
  },
} as const;

function LaneArrow({ lane, vertical }: { lane: Lane; vertical: boolean }) {
  const geometry = GEOMETRY[vertical ? "vertical" : "horizontal"];
  const path = geometry[lane.forward ? "forward" : "backward"];
  return (
    <svg
      aria-hidden="true"
      viewBox={geometry.viewBox}
      className={cn(
        "shrink-0 overflow-visible",
        vertical ? "h-16 w-3.5" : "h-3.5 w-[150px]",
        lane.auto ? "text-action" : "text-primary",
      )}
    >
      <path
        d={path.line}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={lane.auto ? "4 8" : undefined}
      />
      <path
        d={path.head}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LaneLabel({ lane }: { lane: Lane }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-caption font-medium text-muted-foreground">
      {!lane.auto && <MousePointerClick aria-hidden className="size-3.5 text-primary" />}
      {lane.label}
    </span>
  );
}

/** Les voies entre deux outils : à l'horizontale sur grand écran, sinon à la verticale. */
function Connector({ lanes, delay }: { lanes: Lane[]; delay: number }) {
  const style = {
    transitionDelay: `${delay}ms`,
  } as CSSProperties;
  return (
    <div
      className="enc-connector flex flex-col items-start justify-center gap-2 py-2 pl-6 lg:items-center lg:gap-3 lg:py-0 lg:pl-0"
      style={style}
    >
      {lanes.map((lane) => (
        <div
          key={lane.label}
          className="flex items-center gap-3 lg:flex-col-reverse lg:gap-1"
        >
          <span className="lg:hidden">
            <LaneArrow lane={lane} vertical />
          </span>
          <span className="hidden lg:block">
            <LaneArrow lane={lane} vertical={false} />
          </span>
          <LaneLabel lane={lane} />
        </div>
      ))}
    </div>
  );
}

function ToolCard({
  Icon,
  name,
  role,
  items,
  guarantee,
}: {
  Icon: LucideIcon;
  name: string;
  role: string;
  items: string[];
  guarantee: string;
}) {
  return (
    <div className="rounded-panel border border-border bg-surface p-5 shadow-card">
      <div className="flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-lg bg-muted text-foreground">
          <Icon aria-hidden className="size-5" strokeWidth={1.75} />
        </span>
        <div>
          <p className="font-semibold text-foreground">{name}</p>
          <p className="text-caption text-muted-foreground">{role}</p>
        </div>
      </div>
      <ul className="mt-4 space-y-1.5 text-sm text-foreground">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span aria-hidden className="mt-2 size-1 shrink-0 rounded-full bg-border-strong" />
            {item}
          </li>
        ))}
      </ul>
      <p className="mt-4 flex items-start gap-2 rounded-lg bg-success-soft px-3 py-2 text-caption font-medium text-success-strong">
        <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0" />
        {guarantee}
      </p>
    </div>
  );
}

const CONTACT = appConfig.contactEmail;

const EXTRAS: Array<{
  Icon: LucideIcon;
  title: string;
  text: string;
  link?: { href: string; label: string };
}> = [
  {
    Icon: Phone,
    title: "Aircall, en option",
    text: "Activez-le dans les paramètres\u00a0: un clic sur un numéro lance l’appel dans l’application Aircall.",
  },
  {
    Icon: Plug,
    title: "Pas sur Pennylane ou sur Gmail\u00a0?",
    text: `${BRAND} fonctionne aujourd’hui avec ces deux outils. Dites-nous ce que vous utilisez\u00a0: cela nous aide à choisir la suite.`,
    link: { href: `mailto:${CONTACT}`, label: `Écrire à ${CONTACT}` },
  },
];

const LINK =
  "group mt-3 inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-sm text-sm font-semibold text-action hover:text-action-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

function LinkArrow() {
  return (
    <ArrowRight
      aria-hidden
      className="size-4 transition-transform duration-150 group-hover:translate-x-0.5"
    />
  );
}

/** Connexions : ce qui est lu, ce qui part, et à quel rythme. */
export function Connections() {
  const { ref, shown } = useInViewOnce<HTMLDivElement>({ threshold: 0.25 });

  return (
    <section id="connexions" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Connexions"
          title="Branché sur vos outils. Rien à ressaisir."
          lead={`Vos factures viennent de Pennylane, vos relances partent de votre Gmail. ${BRAND} tient le fil entre les deux.`}
        />

        <div
          ref={ref}
          data-shown={shown}
          className="mt-14 grid items-center gap-2 lg:grid-cols-[1fr_auto_auto_auto_1fr] lg:gap-6"
        >
          <div className="enc-connector" style={{ transitionDelay: "0ms" }}>
            <ToolCard
              Icon={BookOpen}
              name="Pennylane"
              role="Votre comptabilité"
              items={[
                "Clients, factures et avoirs",
                "Règlements, pour savoir ce qui est payé",
              ]}
              guarantee={`Lecture seule\u00a0: ${BRAND} n’y écrit rien.`}
            />
          </div>

          <Connector lanes={PENNYLANE_LANES} delay={200} />

          <div
            className="enc-connector flex flex-col items-center py-2 text-center"
            style={{ transitionDelay: "380ms" }}
          >
            <div className="relative grid size-24 place-items-center rounded-full bg-primary shadow-raised">
              <EncrelioMark size={48} inverted />
            </div>
            <p className="mt-3 font-display text-lg font-semibold text-foreground">{BRAND}</p>
            <p className="text-caption text-muted-foreground">le fil de chaque client</p>
          </div>

          <Connector lanes={GMAIL_LANES} delay={560} />

          <div className="enc-connector" style={{ transitionDelay: "740ms" }}>
            <ToolCard
              Icon={Mail}
              name="Gmail"
              role="Votre boîte, ou celle de Google Workspace"
              items={[
                "Relances envoyées de votre adresse, avec la signature de votre choix",
                "Factures en PDF jointes",
                "Réponses rangées dans la fiche du client",
              ]}
              guarantee="Autorisation Google révocable à tout moment."
            />
          </div>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {EXTRAS.map(({ Icon, title, text, link }, i) => (
            <Reveal key={title} delay={i * 100} className="rounded-panel border border-border p-5">
              <Icon aria-hidden className="size-5 text-action" strokeWidth={1.75} />
              <h3 className="mt-3 font-semibold text-foreground">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{text}</p>
              {link && (
                <a href={link.href} className={LINK}>
                  {link.label}
                  <LinkArrow />
                </a>
              )}
            </Reveal>
          ))}
          <Reveal delay={200} className="rounded-panel border border-border p-5">
            <ShieldCheck aria-hidden className="size-5 text-action" strokeWidth={1.75} />
            <h3 className="mt-3 font-semibold text-foreground">Hébergé dans l’Union européenne</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Votre base de données est hébergée dans l’UE. Chaque sous-traitant est
              listé, avec son rôle et sa localisation.
            </p>
            <Link to="/legal/sous-traitants" className={LINK}>
              Voir les sous-traitants
              <LinkArrow />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
